import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ArticleBody from './components/ArticleBody';
import ArticleAside from './components/ArticleAside';
import { ARTICLES } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((item) => item.slug === slug);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      setProgress(total > 0 ? Math.min(100, (doc.scrollTop / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toc = useMemo(() => {
    if (!article) return [];
    return article.body
      .map((section, index) => ({ id: `section-${index}`, text: section.text, type: section.type }))
      .filter((section) => section.type === 'heading')
      .map((section) => ({ id: section.id, text: section.text }));
  }, [article]);

  const related = useMemo(() => {
    if (!article) return [];
    const sameCat = ARTICLES.filter(
      (item) => item.categoryId === article.categoryId && item.id !== article.id,
    );
    const others = ARTICLES.filter(
      (item) => item.categoryId !== article.categoryId && item.id !== article.id,
    );
    return [...sameCat, ...others].slice(0, 3);
  }, [article]);

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: article.title,
        description: article.excerpt,
        inLanguage: 'zh-CN',
        articleSection: article.categoryName,
        keywords: article.tags.join(','),
        author: { '@type': 'Organization', name: article.author },
        publisher: { '@type': 'Organization', name: '黑料百科' },
        datePublished: article.date,
        dateModified: article.date,
        image: article.cover,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '考古档案', item: '/archive' },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
            item: `/archive/${article.slug}`,
          },
        ],
      },
    ];
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="w-full pt-[68px] md:pt-[74px] min-h-[70vh] flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-600 text-2xl">
              <i className="ri-file-unknow-line"></i>
            </span>
            <h1 className="mt-6 font-heading text-2xl text-foreground-950">这条档案不存在</h1>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              它可能已被合并进其他条目，或者链接出了点问题。回到档案库继续翻一翻吧。
            </p>
            <Link
              to="/archive"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回档案库
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Seo
        title={`${article.title} | 黑料百科考古档案`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},黑料百科,考古档案`}
        path={`/archive/${article.slug}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <div
        className="fixed left-0 top-[68px] md:top-[74px] h-0.5 bg-primary-500 z-40 transition-[width] duration-150"
        style={{ width: `${progress}%` }}
        aria-hidden="true"
      ></div>

      <main className="w-full pt-[68px] md:pt-[74px]">
        <header className="w-full border-b border-background-200 bg-background-100 archive-grain">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '考古档案', to: '/archive' },
                { label: article.categoryName, to: `/archive?category=${article.categoryId}` },
                { label: article.id },
              ]}
            />
            <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px] tracking-widest">
              <span className="px-3 py-1.5 rounded-full bg-primary-500/10 text-primary-500">
                {article.categoryName}
              </span>
              <span className="text-foreground-600">{article.id}</span>
              <span className="text-foreground-600">档案员 {article.author}</span>
            </div>
            <h1 className="mt-5 max-w-4xl font-heading text-2xl md:text-4xl leading-snug text-foreground-950">
              {article.title}
            </h1>
            <p className="mt-5 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-600">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line text-primary-500"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line text-primary-500"></i>
                阅读约 {article.readTime} 分钟
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-eye-line text-primary-500"></i>
                {article.views} 次翻阅
              </span>
            </div>
          </div>
        </header>

        <div className="w-full px-4 md:px-8 pt-8 md:pt-12">
          <figure className="w-full max-w-5xl">
            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={`${article.title}的档案配图`}
                title={`${article.title} - 黑料百科考古档案`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </figure>
        </div>

        <section className="w-full bg-background-50 py-10 md:py-16">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-8">
                <ArticleBody body={article.body} />
                <div className="mt-12 pt-8 border-t border-background-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <Link
                    to="/archive"
                    className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回考古档案库
                  </Link>
                  <Link
                    to={`/archive?category=${article.categoryId}`}
                    className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    浏览更多「{article.categoryName}」
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-28">
                  <ArticleAside
                    toc={toc}
                    articleId={article.id}
                    author={article.author}
                    date={article.date}
                    readTime={article.readTime}
                    views={article.views}
                    heat={article.heat}
                    status={article.status}
                    tags={article.tags}
                  />
                </div>
              </aside>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="w-full bg-background-100 border-t border-background-200 py-14 md:py-20">
            <div className="w-full px-4 md:px-8">
              <div className="flex items-end justify-between gap-4 mb-8 md:mb-10">
                <div>
                  <span className="text-[11px] tracking-[0.3em] text-primary-500">
                    RELATED FILES
                  </span>
                  <h2 className="mt-3 font-heading text-2xl md:text-3xl text-foreground-950">
                    顺着这条线继续挖
                  </h2>
                </div>
                <Link
                  to="/archive"
                  className="hidden sm:inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  全部档案
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}